// Web Audio Modules (WAM v2) host — the open, browser-native plugin standard
// (the practical "VST of the web"). Native VST/VST3/AU binaries cannot run in a
// browser sandbox; WAM plugins are ES modules that expose createInstance().
//
// The WAM host runtime is loaded from a CDN on demand so we don't bundle it.
import { AudioEngine } from "./engine";
import { Effect } from "../state/model";
import { uid } from "../util";
import { openModal } from "../ui/modal";

interface LoadedWam { instance: any; node: AudioNode; target: string; url: string; }
const loaded = new Map<string, LoadedWam>(); // fxId -> instance

let hostGroupPromise: Promise<string> | null = null;
async function ensureHost(ctx: AudioContext): Promise<string> {
  if (!hostGroupPromise) {
    hostGroupPromise = (async () => {
      const sdkUrl = "https://esm.sh/@webaudiomodules/sdk@2.0.0";
      const sdk: any = await import(/* @vite-ignore */ sdkUrl);
      const [groupId] = await sdk.initializeWamHost(ctx);
      return groupId;
    })();
  }
  return hostGroupPromise;
}

export async function loadWamEffect(engine: AudioEngine, target: string, url: string): Promise<Effect> {
  const groupId = await ensureHost(engine.ctx);
  const mod: any = await import(/* @vite-ignore */ url);
  const WAM = mod.default;
  if (!WAM?.createInstance) throw new Error("Not a valid WAM v2 module (no createInstance).");
  const instance = await WAM.createInstance(groupId, engine.ctx);
  const node: AudioNode = instance.audioNode;

  const fx: Effect = {
    id: uid("fx"),
    kind: "wam",
    name: instance.descriptor?.name || "WAM Plugin",
    enabled: true,
    params: {},
    wamUrl: url,
  };
  loaded.set(fx.id, { instance, node, target, url });
  engine.addExternalInsert(target, node);
  return fx;
}

export async function openWamGui(fxId: string) {
  const lw = loaded.get(fxId);
  if (!lw) return;
  let gui: HTMLElement | null = null;
  try { gui = await lw.instance.createGui(); } catch {}
  const body = document.createElement("div");
  if (gui) body.append(gui);
  else body.append(Object.assign(document.createElement("p"), { textContent: "This plugin has no GUI." }));
  openModal(lw.instance.descriptor?.name || "Plugin", body, [
    { label: "Close", primary: true, onClick: () => { try { lw.instance.destroyGui?.(gui); } catch {} } },
  ]);
}

/** Remove WAM instances whose descriptor is gone from the project. */
export function reconcileWam(engine: AudioEngine, liveFxIds: Set<string>) {
  for (const [fxId, lw] of loaded) {
    if (!liveFxIds.has(fxId)) {
      engine.removeExternalInsert(lw.target, lw.node);
      try { lw.instance.destroy?.(); } catch {}
      loaded.delete(fxId);
    }
  }
}
